import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { TranslationService } from "../../services/TranslationService";
import ReportsBrackets from "./components/ReportsBrackets";
import ReportsCard from "./components/ReportsCard";
import ClientService from "../../services/ClientService";
import {
  ClientReportPaymentAnalysis,
  ReportPaymentAnalysisResponseType
} from "../client/entities/ClientDebtRecoveryAnalysisRequest";
import Loading from "../shared/components/Loading";
import AdvancedFilters, {
  AdvancedFiltersButton,
  TYPE_REPORT
} from "../shared/components/AdvancedFilters";
import { FilterCollection } from "../../services/FilterService";
import { formatCurrency } from "../../utils/FormatUtils";
import RangedDatepicker from "../shared/components/DateRangePicker";
import ErrorMessage from "../shared/components/Error";
import AdvancedFilterService from "../../services/AdvancedFilterService";

const today = moment();
const firstDayDefault = today.clone().subtract(1, "months").startOf("month");
const firstHalfDayDefault = today
  .clone()
  .subtract(2, "months")
  .startOf("month")
  .add(16, "days");
const lastDayDefault = today.clone().subtract(1, "months").endOf("month");

const getDmy = (date: string) => {
  const d = date.substring(6, 8);
  const m = date.substring(4, 6);
  const y = date.substring(0, 4);
  return [d, m, y];
};

const encodeFiltersToLink = (filters: string[], customIndex?: number) => {
  let counter = customIndex || 0;

  return filters
    .map((value) => {
      counter++;
      return "filter" + counter + "=" + encodeURIComponent(value);
    })
    .join("&");
};

const ReportPaymentAnalysis = () => {
  const [error, setError] = useState(false);
  const [msgError, setMsgError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<ReportPaymentAnalysisResponseType>();

  const initialDaterange = useRef(
    firstDayDefault.format("YYYYMMDD") + "-" + lastDayDefault.format("YYYYMMDD")
  );
  const initialActivityrange = useRef(
    firstHalfDayDefault.format("YYYYMMDD") +
      "-" +
      lastDayDefault.format("YYYYMMDD")
  );

  const [filtersSelected, setFiltersSelected] =
    useState<ClientReportPaymentAnalysis>({
      daterange: initialDaterange.current,
      activityrange: initialActivityrange.current,
      filter: ""
    });

  const [filtersMap, setFiltersMap] = useState<string[]>([]);

  const history = useHistory();
  const { translate, currentLanguage } = TranslationService;

  const navigateClientFilter = useCallback(
    (customerKey: number) => {
      const [startDaterange, endDaterange] =
        filtersSelected.daterange.split("-");
      const [startActivityrange, endActivityrange] =
        filtersSelected.activityrange.split("-");

      const [dsdr, msdr, ysdr] = getDmy(startDaterange);
      const [dedr, medr, yedr] = getDmy(endDaterange || startDaterange);
      const [dsar, msar, ysar] = getDmy(startActivityrange);
      const [dear, mear, year] = getDmy(endActivityrange || startActivityrange);

      let customPath = `/${currentLanguage}/client?filter=filter0%3dselFields%253d-19%2526`;
      const dateRangePath = `selDate%253d${ysdr}${msdr}${dsdr}-${yedr}${medr}${dedr}%26`;
      const extendedPath = "filter1%3dselFields%253d-20%2526";
      const dateRangePathActivity = `selDateActivityType%253d${ysar}${msar}${dsar}-${year}${mear}${dear}%2526`;

      switch (customerKey) {
        case 0:
          customPath = `${customPath + dateRangePath}${filtersMap ? "%26" + encodeURIComponent(encodeFiltersToLink(filtersMap, 1) || "") : ""}`;
          break;
        case 1:
          customPath = `${customPath + dateRangePath + extendedPath + dateRangePathActivity}selActivityType%253d0${filtersMap ? "%26" + encodeURIComponent(encodeFiltersToLink(filtersMap, 1) || "") : ""}`;
          break;
        case 2:
          customPath = `${customPath + dateRangePath + extendedPath + dateRangePathActivity}selActivityType%253d5${filtersMap ? "%26" + encodeURIComponent(encodeFiltersToLink(filtersMap, 1) || "") : ""}`;
          break;
        case 3:
          customPath = `${customPath + dateRangePath + extendedPath + dateRangePathActivity}selActivityType%253d2${filtersMap ? "%26" + encodeURIComponent(encodeFiltersToLink(filtersMap, 1) || "") : ""}`;
          break;
        case 4:
          customPath = `${customPath + dateRangePath + extendedPath + dateRangePathActivity}selActivityType%253d3${filtersMap ? "%26" + encodeURIComponent(encodeFiltersToLink(filtersMap, 1) || "") : ""}`;
          break;
      }
      history.push(customPath);
    },
    [
      currentLanguage,
      filtersSelected.daterange,
      history,
      filtersSelected.activityrange,
      filtersMap
    ]
  );

  const filtersRef = useRef(filtersSelected);

  useEffect(() => {
    filtersRef.current = filtersSelected;
  }, [filtersSelected]);

  const getData = useCallback(async (params?: ClientReportPaymentAnalysis) => {
    const fs = params ?? filtersRef.current; // SIEMPRE el último
    setLoading(true);

    if (!fs.daterange || !fs.activityrange) {
      setMsgError(translate.CompleteToContinue);
      setLoading(false);
      setError(true);
      return;
    }

    const result = await ClientService.getPaymentsAnalysis(fs);
    if (result instanceof Error) {
      setError(true);
      setLoading(false);
      setResponse(undefined);
      return;
    }

    setMsgError(undefined);
    setResponse(result);
    setLoading(false);
    setError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toYmd = (d: Date) =>
    `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;

  const onChangeDaterange = useCallback(
    (start: Date | null, end: Date | null) => {
      if (!start && !end) return; // ignorar emisiones vacías
      const s = start ?? end!,
        e = end ?? start!;
      const vs = toYmd(s),
        ve = toYmd(e);
      const value = vs === ve ? vs : `${vs}-${ve}`;
      setFiltersSelected((prev) =>
        prev.daterange === value ? prev : { ...prev, daterange: value }
      );
    },
    []
  );

  const onChangeActivityrange = useCallback(
    (start: Date | null, end: Date | null) => {
      if (!start && !end) return;
      const s = start ?? end!,
        e = end ?? start!;
      const vs = toYmd(s),
        ve = toYmd(e);
      const value = vs === ve ? vs : `${vs}-${ve}`;
      setFiltersSelected((prev) =>
        prev.activityrange === value ? prev : { ...prev, activityrange: value }
      );
    },
    []
  );

  const applyFilters = (filters: string[]) => {
    setFiltersMap([...filters]);
    const filterEncoded =
      AdvancedFilterService.filterStringsToQueryString(filters);
    setFiltersSelected((p) => ({ ...p, filter: filterEncoded }));
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container-fluid padding reports">
      <h2 className="p-0">{translate.PaymentAnalysis}</h2>
      <div className="d-flex mt-4">
        <label className="col form-label">{translate.PeriodoPagos}:</label>
        <label className="col form-label">{translate.PeriodoGestiones}:</label>
      </div>

      <div className="d-flex justify-content-between mb-2">
        <div className="col align-items-center">
          <div className="col">
            <RangedDatepicker
              onRangeChange={onChangeDaterange}
              defaultValue={filtersSelected.daterange}
            />
          </div>
          <div className="mt-2">
            <AdvancedFiltersButton savedFiltersId={TYPE_REPORT["Payment"]} />
          </div>
        </div>
        <div className="col align-items-center">
          <div className="col">
            <RangedDatepicker
              onRangeChange={onChangeActivityrange}
              defaultValue={filtersSelected.activityrange}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary mt-2"
            onClick={() => getData()}
          >
            {translate.ApplyFilters}
          </button>
        </div>
      </div>

      <AdvancedFilters
        page={FilterCollection.ReportPaymentAnalysis}
        onFilterApply={applyFilters}
      />

      {loading && (
        <div className="container-fluid padding reports d-flex justify-content-center align-items-center vh-100">
          <Loading />
        </div>
      )}
      {!loading && error && (
        <div className="container-fluid padding reports d-flex justify-content-center align-items-center vh-100">
          <ErrorMessage message={msgError} onRefresh={getData} />
        </div>
      )}

      {!loading && !error && response && (
        <div className="d-flex tree">
          <div className="d-flex align-items-center">
            <ReportsCard
              type={"PaymentAnalysis"}
              title={translate.PaymentsInThePeriod}
              action={() => navigateClientFilter(0)}
              customers={response.personCant}
              total={formatCurrency(response.paymentTotal)}
            />
          </div>
          <div className="d-flex gap-4">
            <ReportsBrackets />
            <div className="d-flex flex-column justify-content-center gap-3">
              <ReportsCard
                type={"PaymentAnalysis"}
                title={translate.WithEffectiveActivities}
                action={() => navigateClientFilter(1)}
                customers={response.activitycustomers}
                total={formatCurrency(response.activitypayments)}
              />
              <ReportsCard
                action={() => navigateClientFilter(2)}
                type={"PaymentAnalysis"}
                title={translate.WithAutomaticActivities}
                customers={response.autocustomers}
                total={formatCurrency(response.autopayments)}
              />
              <ReportsCard
                action={() => navigateClientFilter(3)}
                type={"PaymentAnalysis"}
                title={translate.WithUneffectiveActivities}
                customers={response.activitycustomersnoeff}
                total={formatCurrency(response.activitypaymentsnoeff)}
              />
              <ReportsCard
                action={() => navigateClientFilter(4)}
                type={"PaymentAnalysis"}
                title={translate.WithoutActivities}
                customers={response.noactivitycustomers}
                total={formatCurrency(response.noactivitypayments)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPaymentAnalysis;
